// RiskTrendChart.js - Theme-aware risk score trend line for Asset Details Redux V2 (PRD-43)
// Uses Recharts + useThemeColors hook per CLAUDE.md rules

import React, { useMemo } from 'react';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ReferenceLine
} from 'recharts';
import { useThemeColors } from '../../hooks/useThemeColors';
import './RiskTrendChart.css';

function formatAxisDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}`;
}

function ChartTooltip({ active, payload }) {
    if (!active || !payload || !payload.length) return null;
    const row = payload[0].payload;
    const hasBreakdown = row.critical != null || row.high != null || row.medium != null || row.low != null;
    // PRD-59 Tier 2: rows are now sourced from cumulativeRiskTimeline; the
    // discriminator field is `triggerScanType` (legacy `scanType` kept as
    // fallback so the chart still works if reused with the older shape).
    const triggerLabel = row.triggerScanType || row.scanType;
    return (
        <div className="risk-trend-tooltip">
            <div className="risk-trend-tooltip__date">
                {row.dateLabel || formatAxisDate(row.date)}
                {triggerLabel && <span className="risk-trend-tooltip__type"> · {triggerLabel}</span>}
            </div>
            <div className="risk-trend-tooltip__score">
                Score: <strong>{row.score}</strong>
                {row.level && <span className="risk-trend-tooltip__level">{row.level}</span>}
            </div>
            {hasBreakdown && (
                <div className="risk-trend-tooltip__breakdown">
                    <span>{row.critical || 0}C</span>
                    <span>{row.high || 0}H</span>
                    <span>{row.medium || 0}M</span>
                    <span>{row.low || 0}L</span>
                </div>
            )}
        </div>
    );
}

/**
 * @param {object} props
 * @param {Array<{date, score, level?}>} props.data - Time-ordered oldest→newest
 * @param {number} [props.currentScore]
 * @param {number} [props.baselineScore]
 * @param {string} [props.emptyMessage]
 * @param {(point: object) => void} [props.onPointClick] - PRD-133: called with the clicked point's
 *        data row (has `date`/score/level) so the parent can fetch a change-attribution explanation.
 */
function RiskTrendChart({ data = [], currentScore, baselineScore, emptyMessage, onPointClick }) {
    const colors = useThemeColors();

    // PRD-133: Recharts fires LineChart onClick with the nearest point's activePayload — the most
    // reliable way to make trend points clickable across versions.
    const handleChartClick = (state) => {
        if (onPointClick && state && state.activePayload && state.activePayload.length) {
            onPointClick(state.activePayload[0].payload);
        }
    };

    const chartData = useMemo(() => {
        return (data || [])
            .filter((d) => d && d.score != null && d.date)
            .map((d) => ({
                ...d,
                dateLabel: formatAxisDate(d.date),
            }));
    }, [data]);

    if (!chartData.length) {
        return (
            <div className="risk-trend-empty">
                {emptyMessage || 'Not enough scan history yet.'}
            </div>
        );
    }

    // PRD-51 + PRD-59: higher score = riskier. Y axis runs 0-100, ascending.
    const lineColor = colors.accent?.primary || '#0891B2';
    const gridColor = colors.border?.dark || '#CBD5E1';
    const axisColor = colors.text?.muted || '#64748B';

    return (
        <div className="risk-trend-chart" style={onPointClick ? { cursor: 'pointer' } : undefined}>
            <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData} margin={{ top: 8, right: 12, left: -12, bottom: 0 }} onClick={handleChartClick}>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={gridColor}
                        vertical={false}
                    />
                    <XAxis
                        dataKey="dateLabel"
                        stroke={axisColor}
                        tick={{ fontSize: 11, fill: axisColor }}
                        tickLine={false}
                        axisLine={{ stroke: gridColor }}
                    />
                    <YAxis
                        domain={[0, 'auto']}
                        stroke={axisColor}
                        tick={{ fontSize: 11, fill: axisColor }}
                        tickLine={false}
                        axisLine={{ stroke: gridColor }}
                        width={36}
                    />
                    <Tooltip content={<ChartTooltip />} cursor={{ stroke: axisColor, strokeDasharray: '2 2' }} />

                    {/* PRD-51: Risk-band reference lines (higher = riskier) */}
                    <ReferenceLine y={80} stroke={colors.severity?.critical || '#DC2626'} strokeDasharray="2 4" strokeOpacity={0.4} />
                    <ReferenceLine y={40} stroke={colors.severity?.medium || '#F59E0B'} strokeDasharray="2 4" strokeOpacity={0.4} />

                    {baselineScore != null && (
                        <ReferenceLine
                            y={baselineScore}
                            stroke={colors.accent?.secondary || '#0D9488'}
                            strokeDasharray="4 4"
                            label={{
                                value: `AI baseline: ${baselineScore}`,
                                position: 'insideTopRight',
                                fill: colors.text?.muted || '#64748B',
                                fontSize: 10
                            }}
                        />
                    )}

                    <Line
                        type="monotone"
                        dataKey="score"
                        stroke={lineColor}
                        strokeWidth={2.5}
                        dot={{ fill: lineColor, r: 3, strokeWidth: 0 }}
                        activeDot={{ r: 5, strokeWidth: 2, stroke: colors.bg?.card || '#FFFFFF' }}
                        isAnimationActive={true}
                        animationDuration={420}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
}

export default RiskTrendChart;
